import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { message } from "antd";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import HeaderBread from "../../../../../components/HeaderBread";
import Layout from "../../../../../Layout";
import Logs from "../../../../../components/Logs";
import SwitchBar from "../../../../../components/Pages/SwitchBar";
import SortableTable from "../../../../../components/UI/SortableTable";
import SearchField from "../../../../../components/SearchField";
import DeletePop from "../../../../../constants/ErrorPermissionModal";
import EntertainmentRvcInput from "../../../../../components/EntertainmentRvcInput";
import { useDataLayerValue } from "../../../../../DataLayer/DataLayer";
import { checkPermission } from "../../../../../utils/Validation/checkPermission";
import { useDebounce } from "../../../../../hooks";
import { addlog } from "../../../../../utils/ApiCalls/Logs";
import { ENTERTAINMENT_LOG_CATEGORIES, LOG_ACTIONS, LOG_TYPES } from "../../../../../utils/Constant/LogConstant";
import {
  getEntertainmentRvc,
  getEntertainmentItemizers,
  deleteEntertainmentItemizer,
  getEntertainmentMenuItems,
  updateEntertainmentMenuItem,
} from "../../../../../utils/ApiCalls/BackOffice/Entertainment";

const LIST_PATH = "/shore/back-office/entertainment/revenue-center";
const MANAGEMENT_PATH = "/shore/back-office/entertainment/management";

const EntertainmentRevenueCenterDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [{ permission, userDetails }, dispatch] = useDataLayerValue();

  const basePath = `${LIST_PATH}/${params.id}`;

  const [topTab, setTopTab] = useState(location.state?.topTab || "overview");
  const [rvc, setRvc] = useState(null);

  const [itemizers, setItemizers] = useState([]);
  const [itemizerPage, setItemizerPage] = useState(1);
  const [itemizerPageSize, setItemizerPageSize] = useState(10);
  const [itemizerCount, setItemizerCount] = useState(0);
  const [itemizerSearch, setItemizerSearch] = useState("");
  const debouncedItemizerSearch = useDebounce(itemizerSearch, 1000);

  const [menuItems, setMenuItems] = useState([]);
  const [menuItemPage, setMenuItemPage] = useState(1);
  const [menuItemPageSize, setMenuItemPageSize] = useState(10);
  const [menuItemCount, setMenuItemCount] = useState(0);
  const [menuItemSearch, setMenuItemSearch] = useState("");
  const debouncedMenuItemSearch = useDebounce(menuItemSearch, 1000);
  const [menuItemStatus, setMenuItemStatus] = useState(1);

  const loadRvc = async () => {
    dispatch({ type: "SET_LOADING", loading: true });
    const data = await getEntertainmentRvc(params.id);
    if (data.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
    } else {
      setRvc(data);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    loadRvc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  // itemizers are also needed by the Menu Item tab (to resolve each row's
  // itemizer code), so they're loaded unpaginated regardless of active tab
  const loadItemizers = async (targetPage = itemizerPage) => {
    if (!rvc?.id) return;
    const res = await getEntertainmentItemizers({
      rvcId: rvc.id,
      page: targetPage,
      limit: itemizerPageSize,
      ...(debouncedItemizerSearch ? { search: debouncedItemizerSearch } : {}),
    });
    if (res?.error) return;
    setItemizers(res.data || []);
    setItemizerCount(res.totalCount || 0);
  };

  const loadMenuItems = async (targetPage = menuItemPage) => {
    if (!rvc?.id) return;
    const res = await getEntertainmentMenuItems({
      rvcId: rvc.id,
      page: targetPage,
      limit: menuItemPageSize,
      ...(debouncedMenuItemSearch ? { search: debouncedMenuItemSearch } : {}),
      ...(menuItemStatus !== "" ? { isActive: menuItemStatus } : {}),
    });
    if (res?.error) return;
    setMenuItems(res.data || []);
    setMenuItemCount(res.totalCount || 0);
  };

  useEffect(() => {
    setItemizerPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedItemizerSearch]);

  useEffect(() => {
    setMenuItemPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedMenuItemSearch, menuItemStatus]);

  useEffect(() => {
    if (!rvc?.id) return;
    loadItemizers(itemizerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rvc?.id, itemizerPage, itemizerPageSize, debouncedItemizerSearch]);

  useEffect(() => {
    if (!rvc?.id) return;
    loadMenuItems(menuItemPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rvc?.id, menuItemPage, menuItemPageSize, debouncedMenuItemSearch, menuItemStatus]);

  const handleDeleteItemizer = async (id) => {
    if (!checkPermission(permission, "delete_per", "Revenue Center", window?.location?.pathname)) {
      message.error("Missing Permission for delete");
      return;
    }
    const itemizerCode = itemizers.find((it) => it.id === id)?.itemizerCode;
    const data = await deleteEntertainmentItemizer(id);
    if (data?.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
      return;
    }
    addlog(
      LOG_ACTIONS.REMOVED,
      LOG_TYPES.ENTERTAINMENT_RVC,
      {
        id,
        rvcId: rvc?.id,
        title: `${rvc?.name ? rvc.name + " - " : ""}${itemizerCode}`,
        category: ENTERTAINMENT_LOG_CATEGORIES.ITEMIZER,
      },
      userDetails.id
    );
    loadItemizers(itemizerPage);
  };

  const handleToggleMenuItemStatus = async (row) => {
    if (!checkPermission(permission, "update_per", "Revenue Center", window?.location?.pathname)) {
      message.error("Missing Permission for edit");
      return;
    }
    const nextActive = row.isActive === 1 ? 0 : 1;
    const data = await updateEntertainmentMenuItem(row.id, {
      isActive: nextActive,
    });
    if (data?.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
      return;
    }
    addlog(
      `${rvc?.name ? rvc.name + " - " : ""}${row.name} is ` +
        (nextActive === 1 ? LOG_ACTIONS.ACTIVATED : LOG_ACTIONS.DEACTIVATED),
      LOG_TYPES.ENTERTAINMENT_RVC,
      {
        id: row.id,
        rvcId: rvc?.id,
        title: `${rvc?.name ? rvc.name + " - " : ""}${row.name}`,
        category: ENTERTAINMENT_LOG_CATEGORIES.MENU_ITEM,
      },
      userDetails.id
    );
    loadMenuItems(menuItemPage);
  };

  const itemizerColumns = [
    {
      title: "SI",
      key: "sl",
      align: "center",
      width: 60,
      render: (value, row, index) => (itemizerPage - 1) * itemizerPageSize + index + 1,
    },
    { title: "Itemizer ID", key: "itemizerId", align: "center" },
    { title: "Itemizer Code", key: "itemizerCode" },
    { title: "Description", key: "itemizerDescription" },
    {
      title: "Action",
      key: "operation",
      align: "center",
      width: 100,
      render: (value, row) => (
        <div className="table_action_btn">
          <button
            className="btn"
            onClick={() =>
              navigate(`${basePath}/itemizer/edit/${row.id}`)
            }
          >
            <i className="ri-edit-2-line" data-toggle="tooltip" title="Edit"></i>
          </button>
          <DeletePop onConfirm={() => handleDeleteItemizer(row.id)}>
            <div>
              <button className="btn delete_btn">
                <i className="ri-delete-bin-line" data-toggle="tooltip" title="Delete"></i>
              </button>
            </div>
          </DeletePop>
        </div>
      ),
    },
  ];

  const menuItemColumns = [
    {
      title: "SI",
      key: "sl",
      align: "center",
      width: 60,
      render: (value, row, index) => (menuItemPage - 1) * menuItemPageSize + index + 1,
    },
    { title: "Menu Item Number", key: "itemNumber", align: "center" },
    { title: "Menu Item Name", key: "name" },
    { title: "Menu Item Description", key: "description" },
    {
      title: "Itemizer",
      key: "itemizerId",
      align: "center",
      render: (value) => {
        const itemizer = itemizers.find((it) => it.id === value);
        return itemizer ? `${itemizer.itemizerCode} (${itemizer.itemizerId})` : value;
      },
    },
    {
      title: "Price",
      key: "price",
      align: "right",
    },
    {
      title: "Status",
      key: "isActive",
      align: "center",
      render: (value, row) => (
        <Switch
          checked={value === 1}
          onChange={() => handleToggleMenuItemStatus(row)}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: "#dc3545" },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#dc3545",
            },
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "operation",
      align: "center",
      width: 100,
      render: (value, row) => (
        <div className="table_action_btn">
          <button
            className="btn"
            disabled={row.isActive !== 1}
            onClick={() =>
              row.isActive === 1 && navigate(`${basePath}/menu-item/edit/${row.id}`)
            }
          >
            <i className="ri-edit-2-line" data-toggle="tooltip" title="Edit"></i>
          </button>
        </div>
      ),
    },
  ];

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Revenue Center", route: LIST_PATH },
    { title: `Edit Revenue Center`, route: basePath },
  ];

  return (
    <Layout type={"read_per"} module_data={"Revenue Center"}>
      <div>
        <HeaderBread navigation={navigation} title={"Revenue Center" + (rvc?.name ? ` - ${rvc.name}` : "")} />
      </div>
      <section className="content" style={{ marginTop: "20px" }}>
        <div className="container-fluid">
          <SwitchBar
            tabs={[
              { title: "Overview", value: "overview" },
              { title: "Itemizer", value: "itemizer" },
              { title: "Menu Item", value: "menuItem" },
              { title: "History Logs", value: "logs" },
            ]}
            activeTab={topTab}
            setActiveTab={(tab) => {
              setTopTab(tab);
              // keep this history entry's state in sync so navigating away
              // (e.g. to edit a menu item) and back with the browser's
              // back button restores the tab the user was actually on
              navigate(location.pathname, { replace: true, state: { topTab: tab } });
            }}
          />

          {topTab === "overview" && rvc && (
            <EntertainmentRvcInput rvc={rvc} onSuccess={loadRvc} readOnly />
          )}

          {topTab === "logs" && (
            <Logs
              type={LOG_TYPES.ENTERTAINMENT_RVC}
              slug={`${MANAGEMENT_PATH}/edit/`}
              extendedColors
              removedClickable
            />
          )}

          {topTab === "itemizer" && (
            <div className="card">
              <div className="card-header two_item">
                <h5 className="card-title">Itemizer</h5>
                <div className="two_item">
                  <Link
                    to={`${basePath}/itemizer/add`}
                    className="btn add_v_btn mx-2"
                  >
                    Add New
                  </Link>
                </div>
              </div>
              <div className="card-body">
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "flex-end",
                    marginBottom: "12px",
                  }}
                >
                  <div style={{ maxWidth: 220 }}>
                    <SearchField
                      value={itemizerSearch}
                      setValue={(e) => setItemizerSearch(e.target.value)}
                      SearchValue={() => setItemizerPage(1)}
                      placeholder="Search by itemizer ID or code"
                    />
                  </div>
                </div>
                <div className="table_holder">
                  <SortableTable
                    columns={itemizerColumns}
                    rows={itemizers}
                    page={itemizerPage}
                    setPage={setItemizerPage}
                    count={itemizerCount}
                    pageSize={itemizerPageSize}
                    setPageSize={setItemizerPageSize}
                  />
                </div>
              </div>
            </div>
          )}

          {topTab === "menuItem" && (
            <div className="card">
              <div className="card-header two_item">
                <h5 className="card-title">Menu Item</h5>
                <div className="two_item">
                  {itemizerCount === 0 ? (
                    <Tooltip title="Add at least one itemizer" enterDelay={0} enterTouchDelay={0}>
                      <span
                        className="btn add_v_btn mx-2"
                        style={{ opacity: 0.5, cursor: "not-allowed" }}
                      >
                        Add New
                      </span>
                    </Tooltip>
                  ) : (
                    <Link
                      to={`${basePath}/menu-item/add`}
                      className="btn add_v_btn mx-2"
                    >
                      Add New
                    </Link>
                  )}
                </div>
              </div>
              <div className="card-body">
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    marginBottom: "12px",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <Select
                    style={{ width: "160px", height: "40px" }}
                    displayEmpty
                    value={menuItemStatus}
                    onChange={(e) => setMenuItemStatus(e.target.value)}
                    renderValue={menuItemStatus !== "" ? undefined : () => "All Status"}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={0}>Inactive</MenuItem>
                  </Select>
                  <div style={{ maxWidth: 220 }}>
                    <SearchField
                      value={menuItemSearch}
                      setValue={(e) => setMenuItemSearch(e.target.value)}
                      SearchValue={() => setMenuItemPage(1)}
                      placeholder="Search by name or item number"
                    />
                  </div>
                </div>
                <div className="table_holder">
                  <SortableTable
                    columns={menuItemColumns}
                    rows={menuItems}
                    page={menuItemPage}
                    setPage={setMenuItemPage}
                    count={menuItemCount}
                    pageSize={menuItemPageSize}
                    setPageSize={setMenuItemPageSize}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default EntertainmentRevenueCenterDetail;
